import React, { useEffect, useState } from "react";
import {
  EMPTY_FORM,
  PLAN_TYPE_OPTIONS,
  VALIDITY_OPTIONS,
  normalizePlan,
  normalizePlanAccess,
} from "./plansManagement.utils";
import { usePermission } from "../../hooks/usePermissions";
import { formatAdminDate, formatAdminDateTime } from "../../utils/datetime";

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlanModal = ({ mode, plan, onClose, onSave, saving, planAccessOptions = [] }) => {
  const {hasPermission} = usePermission();
  const [form, setForm] = useState(mode === "edit" && plan ? normalizePlan(plan) : { ...EMPTY_FORM });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(mode === "edit" && plan ? normalizePlan(plan) : { ...EMPTY_FORM });
    setErrors({});
  }, [mode, plan]);

  const pFull = Number(form.price_full) || 0;
  const pDisc = Number(form.price_discounted) || 0;
  const discountPct = pFull > 0 ? Math.round(((pFull - pDisc) / pFull) * 100) : 0;
  const discountInvalid = pDisc > pFull && pFull > 0;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAccessToggle = (id) => {
    setForm((prev) => {
      const current = normalizePlanAccess(prev.plan_access_id);
      return {
        ...prev,
        plan_access_id: current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id],
      };
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(form.name || "").trim()) nextErrors.name = "Plan name is required";
    if (form.price_full === "" || form.price_full == null) nextErrors.price_full = "Full price is required";
    if (form.price_discounted === "" || form.price_discounted == null) nextErrors.price_discounted = "Discounted price is required";
    if (discountInvalid) nextErrors.price_discounted = "Discounted price must be ≤ full price";
    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    onSave({
      ...form,
      plan_access_id: normalizePlanAccess(form.plan_access_id),
    });
  };

  return (
    <div className="pm-modal-overlay">
      <div className="pm-modal">
        <div className="pm-modal-header">
          <h2>{mode === "add" ? "+ Add New Plan" : "✏️ Edit Plan"}</h2>
          <button className="pm-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="pm-modal-body">
          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Plan Name <span className="required">*</span></label>
              <input
                className={`pm-input${errors.name ? " error" : ""}`}
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g. Equity Pro Plan"
              />
              {errors.name && <span className="pm-input-error">{errors.name}</span>}
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Description</label>
              <textarea
                className="pm-textarea"
                value={form.description_text}
                onChange={(e) => handleChange("description_text", e.target.value)}
                placeholder="Brief description of the plan..."
              />
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Full Price (₹) <span className="required">*</span></label>
              <input
                className={`pm-input${errors.price_full ? " error" : ""}`}
                type="number"
                min="0"
                value={form.price_full}
                onChange={(e) => handleChange("price_full", e.target.value)}
                placeholder="e.g. 999"
              />
              {errors.price_full && <span className="pm-input-error">{errors.price_full}</span>}
            </div>
            <div className="pm-form-group">
              <label>Discounted Price (₹) <span className="required">*</span></label>
              <input
                className={`pm-input${errors.price_discounted ? " error" : ""}`}
                type="number"
                min="0"
                value={form.price_discounted}
                onChange={(e) => handleChange("price_discounted", e.target.value)}
                placeholder="e.g. 499"
              />
              {errors.price_discounted && <span className="pm-input-error">{errors.price_discounted}</span>}
            </div>
          </div>

          {form.price_full !== "" && form.price_discounted !== "" && (
            <div className={`pm-discount-hint${discountInvalid ? " warn" : ""}`}>
              {discountInvalid
                ? "⚠️ Discounted price must be less than or equal to full price"
                : `✅ You are giving ${discountPct}% discount`}
            </div>
          )}

          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Validity</label>
              <select className="pm-select" value={form.validity} onChange={(e) => handleChange("validity", e.target.value)}>
                {VALIDITY_OPTIONS.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="pm-form-group">
              <label>Plan Type</label>
              <select className="pm-select" value={form.plan_type} onChange={(e) => handleChange("plan_type", e.target.value)}>
                {PLAN_TYPE_OPTIONS.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Tip Message (Badge)</label>
              <input
                className="pm-input pm-min-h-64"
                value={form.tip_message}
                onChange={(e) => handleChange("tip_message", e.target.value)}
                placeholder="e.g. Popular, Economical, Advantage pack"
              />
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Active Status</label>
              <select className="pm-select" value={form.active_status} onChange={(e) => handleChange("active_status", e.target.value)}>
                <option value="Yes">Active</option>
                <option value="No">Inactive</option>
              </select>
            </div>
            <div className="pm-form-group">
              <label>Display on App</label>
              <select className="pm-select" value={form.display} onChange={(e) => handleChange("display", e.target.value)}>
                <option value="Yes">Visible</option>
                <option value="No">Hidden</option>
              </select>
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Plan Access</label>
              <div className="pm-features-list">
                {planAccessOptions.map((option) => {
                  const selected = normalizePlanAccess(form.plan_access_id).includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`pm-feature-tag ${selected ? "selected" : ""}`}
                      onClick={() => handleAccessToggle(option.id)}
                    >
                      {selected ? "✓ " : ""}
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Terms & Conditions</label>
              <textarea
                className="pm-textarea"
                value={form.tnc}
                onChange={(e) => handleChange("tnc", e.target.value)}
                placeholder="Enter terms and conditions..."
                style={{ minHeight: 64 }}
              />
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Razorpay Plan ID</label>
              <input
                className="pm-input"
                value={form.razorpay_plan}
                onChange={(e) => handleChange("razorpay_plan", e.target.value)}
                placeholder="plan_XXXXXXXX"
              />
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Button Code</label>
              <textarea
                className="pm-textarea pm-textarea-code"
                value={form.button_code}
                onChange={(e) => handleChange("button_code", e.target.value)}
                placeholder="<form><script ...></script></form>"
              />
            </div>
          </div>
        </div>

        <div className="pm-modal-footer">
          <button className="pm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pm-btn-save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : mode === "add" ? "+ Create Plan" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewModal = ({ plan, onClose }) => {
  const fields = [
    { label: "Plan Name", value: plan.name },
    { label: "ID", value: `#${plan.id}` },
    { label: "Plan Type", value: plan.plan_type },
    { label: "Validity", value: plan.validity },
    { label: "Full Price", value: `₹${Number(plan.price_full).toLocaleString("en-IN")}` },
    { label: "Discounted Price", value: `₹${Number(plan.price_discounted).toLocaleString("en-IN")}` },
    { label: "Tip Message", value: plan.tip_message || "—" },
    { label: "Active Status", value: plan.active_status },
    { label: "Display", value: plan.display },
    { label: "Razorpay Plan ID", value: plan.razorpay_plan || "—" },
    { label: "Created", value: plan.created || "—" },
    { label: "Updated", value: formatAdminDate(plan.updated, "—") },
    { label: "Description", value: plan.description_text || "—", full: true },
    { label: "Terms & Conditions", value: plan.tnc || "—", full: true },
    { label: "Button Code", value: plan.button_code || "—", full: true, code: true },
  ];

  return (
    <div className="pm-modal-overlay">
      <div className="pm-modal">
        <div className="pm-modal-header">
          <h2>🔍 Plan Details</h2>
          <button className="pm-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="pm-modal-body">
          <div className="pm-view-grid">
            {fields.map((field) => (
              <div key={field.label} className={`pm-view-item${field.full ? " full" : ""}`}>
                <span className="pm-view-item-label">{field.label}</span>
                <span className={`pm-view-item-value${field.code ? " code" : ""}`}>{field.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pm-modal-footer">
          <button className="pm-btn-save" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const DeleteModal = ({ plan, onClose, onConfirm }) => (
  <div className="pm-modal-overlay">
    <div className="pm-modal pm-modal-max-400">
      <div className="pm-modal-header">
        <h2>Delete Plan</h2>
        <button className="pm-modal-close" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <div className="pm-modal-body pm-modal-body-centered">
        <div className="pm-confirm-icon">🗑️</div>
        <p className="pm-confirm-msg">Are you sure you want to delete</p>
        <p className="pm-confirm-name">"{plan.name}"?</p>
        <p className="pm-error-text">
          This action cannot be undone.
        </p>
      </div>
      <div className="pm-modal-footer pm-justify-center">
        <button className="pm-btn-cancel" onClick={onClose}>Cancel</button>
        <button
          className="pm-btn-delete"
          onClick={() => onConfirm(plan.id)}
        >
          Yes, Delete
        </button>
      </div>
    </div>
  </div>
);

const SkeletonRows = () => (
  <>
    {[1, 2, 3, 4, 5].map((index) => (
      <tr key={index} className="pm-skeleton-row">
        {[40, 120, 70, 60, 80, 80, 90, 70, 100].map((width, cellIndex) => (
          <td key={cellIndex}>
            <span className="pm-skel" style={{ width }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const ThCol = ({ col, label, center, sortKey, sortDir, onSort }) => {
  const sortIcon = sortKey === col ? (sortDir === "asc" ? "▲" : "▼") : "⇅";

  return (
    <th
      className={`sortable${center ? " center" : ""}`}
      onClick={() => onSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="pm-th-inner">
        {label}
        <span className="pm-sort-icon">{sortIcon}</span>
      </span>
    </th>
  );
};

const PlansManagementView = ({
  loading,
  error,
  filteredCount,
  searchQuery,
  onSearchChange,
  onSearchApply,
  typeFilter,
  onTypeFilterChange,
  activeFilter,
  onActiveFilterChange,
  displayFilter,
  onDisplayFilterChange,
  sortKey,
  sortDir,
  onSort,
  currentPage,
  onPageChange,
  totalPages,
  paginatedPlans,
  onAdd,
  onEdit,
  onDelete,
  modalMode,
  selectedPlan,
  onCloseModal,
  onSavePlan,
  onDeletePlan,
  saving,
  planAccessOptions = [],
}) => {
  const { hasPermission } = usePermission();
  return (
    <div className="pm-container">
      <div className="pm-page-header">
        <div className="pm-header-left">
          <h1 className="pm-page-title">Plans Management</h1>
          <p className="pm-page-subtitle">{filteredCount} plan{filteredCount !== 1 ? "s" : ""} found</p>
        </div>
        <div className="pm-header-right">
          {hasPermission("CREATE_PLANS") && (
            <button className="pm-btn-add" onClick={onAdd}>
              <PlusIcon /> Add New Plan
            </button>
          )}
        </div>
      </div>

      {error && <div className="pm-alert">{error}</div>}

      <div className="pm-table-filters">
        <div className="pm-filters-left">
          <span className="pm-filter-count">{filteredCount} result{filteredCount !== 1 ? "s" : ""}</span>
          <select className="pm-filter-select" value={typeFilter} onChange={(e) => onTypeFilterChange(e.target.value)}>
            <option value="">All Plan Types</option>
            {PLAN_TYPE_OPTIONS.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          <select className="pm-filter-select" value={activeFilter} onChange={(e) => onActiveFilterChange(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="Yes">Active</option>
            <option value="No">Inactive</option>
          </select>
          <select className="pm-filter-select" value={displayFilter} onChange={(e) => onDisplayFilterChange(e.target.value)}>
            <option value="">All Display</option>
            <option value="Yes">Visible</option>
            <option value="No">Hidden</option>
          </select>
        </div>

        <div className="pm-filters-right">
          <div className="pm-search-wrap">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by plan name..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearchApply()}
            />
          </div>
          <button className="pm-btn-search" onClick={onSearchApply}>Search</button>
        </div>
      </div>

      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead>
            <tr>
              <ThCol col="id" label="ID" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="name" label="Plan Name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="plan_type" label="Type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="validity" label="Validity" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="price_full" label="Full Price" center sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="price_discounted" label="Discounted" center sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="tip_message" label="Badge" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <ThCol col="active_status" label="Status" center sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : paginatedPlans.length === 0 ? (
              <tr className="pm-empty-row">
                <td colSpan={9}>
                  <div className="pm-empty-icon">📋</div>
                  <h3>No plans found</h3>
                  <p>Try adjusting your filters or search query.</p>
                </td>
              </tr>
            ) : (
              paginatedPlans.map((plan) => {
                const priceFull = Number(plan.price_full) || 0;
                const priceDisc = Number(plan.price_discounted) || 0;
                const discountPct = priceFull > 0 ? Math.round(((priceFull - priceDisc) / priceFull) * 100) : 0;

                return (
                  <tr key={plan.id}>
                    <td><span className="pm-cell-id">#{plan.id}</span></td>
                    <td>
                      <div className="pm-cell-name">{plan.name}</div>
                      <div className="pm-cell-sub">{plan.razorpay_plan || "-"}</div>
                    </td>
                    <td>
                      <span className={`pm-type-badge ${plan.plan_type === "One Time" ? "one-time" : "subscription"}`}>
                        {plan.plan_type === "One Time" ? "⚡" : "🔄"} {plan.plan_type}
                      </span>
                    </td>
                    <td>{plan.validity || "-"}</td>
                    <td className="center pm-cell-price">
                      <span className="pm-price-full">₹{priceFull.toLocaleString("en-IN")}</span>
                    </td>
                    <td className="center pm-cell-price">
                      <span className="pm-price-disc">₹{priceDisc.toLocaleString("en-IN")}</span>
                      {discountPct > 0 && <span className="pm-price-off">{discountPct}% off</span>}
                    </td>
                    <td>
                      {plan.tip_message
                        ? <span className="pm-tip-badge">🏷️ {plan.tip_message}</span>
                        : <span className="pm-text-muted-dash">—</span>}
                    </td>
                    <td className="center">
                      <span className={`pm-status-badge ${plan.active_status === "Yes" ? "active" : "inactive"}`}>
                        {plan.active_status === "Yes" ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td className="center">
                      <div className="pm-actions-cell">
                        {hasPermission("MODIFY_PLANS") && (
                          <button className="pm-icon-btn edit" title="Edit" onClick={() => onEdit(plan)}>
                            <PencilIcon />
                          </button>
                        )}
                        {hasPermission("DELETE_PLANS") && (
                          <button className="pm-icon-btn delete" title="Delete" onClick={() => onDelete(plan)}>
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && filteredCount > 0 && (
          <div className="pm-table-footer">
            <span>Showing {paginatedPlans.length} of {filteredCount} plans</span>

            {totalPages > 1 && (
              <div className="pm-pagination">
                <button className="pm-page-btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`pm-page-btn ${currentPage === page ? "active" : ""}`}
                        onClick={() => onPageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  }

                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="pm-pagination-ellipsis">
                        ...
                      </span>
                    );
                  }

                  return null;
                })}
                <button className="pm-page-btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>
                  &gt;
                </button>
              </div>
            )}

            <span className="pm-pagination-count">
              Last refreshed: {formatAdminDateTime(new Date())}
            </span>
          </div>
        )}
      </div>

      {(modalMode === "add" || modalMode === "edit") && (
        <PlanModal
          mode={modalMode}
          plan={selectedPlan}
          onClose={onCloseModal}
          onSave={onSavePlan}
          saving={saving}
          planAccessOptions={planAccessOptions}
        />
      )}
      {modalMode === "view" && selectedPlan && (
        <ViewModal plan={selectedPlan} onClose={onCloseModal} />
      )}
      {modalMode === "delete" && selectedPlan && (
        <DeleteModal plan={selectedPlan} onClose={onCloseModal} onConfirm={onDeletePlan} />
      )}
    </div>
  );
};

export default PlansManagementView;
